import { Grid, Stack, Tabs, TabsPanel } from '@customerio/pluma-components/react';
import type {
  ServicesApiGetEnvHealthQueryParams,
  ServicesApiEnvHealthResponse,
  ServicesApiEnvHealthDailyStats,
} from '@/services/api/services-api';
import { servicesQuery } from '@/services/api';
import { getWorkspaceIdFromCurrentUrl } from '@/context/current-workspace';
import { formatThroughput } from '../utils';
import { AlertsPanel } from './alerts-panel';
import { StatsPanel } from './stats-panel';
import { TasksPanel } from './tasks-panel';
import { TopEventsPanel } from './top-events-panel';
import { SegmentsPanel } from './segments-panel';
import { SummaryPanel } from './summary-panel';
import { AISummaryPanel } from './ai-summary-panel';
import { RecommendationsPanel } from './recommendations-panel';
import { StatCard } from './stat-card';
import { ServicesTabContent } from './services-tab-content';
import { MessagingTabContent } from './messaging-tab-content';
import * as styles from './index.css';

function outgoingThroughputTooltip(stats: ServicesApiEnvHealthDailyStats): string {
  const base = 'How fast outgoing messages (email, push, SMS, webhooks) are being sent right now.';
  const total = stats.egress_throughput_per_second;
  const outgoing = stats.outgoing_throughput_per_second;
  if (total <= 0 || outgoing <= 0) return base;
  const internalShare = (total - outgoing) / total;
  if (internalShare < 0.2) return base;
  return `${base} Some processing capacity is currently used by internal operations (profile merging, object updates).`;
}

const TABS = [
  { value: 'overview', text: 'Overview' },
  { value: 'services', text: 'Services' },
  { value: 'data', text: 'Data' },
  { value: 'messaging', text: 'Messaging' },
] as const;

interface HealthOverviewProps {
  data: ServicesApiEnvHealthResponse;
  simulate?: ServicesApiGetEnvHealthQueryParams['simulate'];
  activeTab: string;
  onTabChange: (value: string | null) => void;
}

export function HealthOverview({ data, simulate, activeTab, onTabChange }: HealthOverviewProps) {
  const workspaceId = getWorkspaceIdFromCurrentUrl();
  const goToServices = () => onTabChange('services');

  const topEventsQuery = servicesQuery.getEnvHealthTopEvents.useQuery({
    queryKey: ['env-health-top-events', workspaceId, simulate],
    queryData: { params: { environment_id: workspaceId ?? 0 }, query: { simulate } },
    refetchInterval: 120_000,
    enabled: workspaceId != null && activeTab === 'data',
  });
  const topEvents = topEventsQuery.data?.body?.top_events;

  const segmentsQuery = servicesQuery.getEnvHealthSegments.useQuery({
    queryKey: ['env-health-segments', workspaceId, simulate],
    queryData: { params: { environment_id: workspaceId ?? 0 }, query: { simulate } },
    refetchInterval: 120_000,
    enabled: workspaceId != null,
  });
  const segments = segmentsQuery.data?.body?.segments;

  return (
    <Grid columns="340px 1fr" gap="300" alignItems="flex-start">
      <Stack gap="200" className={styles.sidebarSticky}>
        <SummaryPanel data={data} />
        <RecommendationsPanel data={data} segments={segments} />
      </Stack>
      <Tabs tabs={[...TABS]} activeTab={activeTab} onChange={onTabChange}>
        <TabsPanel value="overview">
          <Stack gap="200" pt="200">
            <AISummaryPanel data={data} />
            <AlertsPanel alerts={data.alerts} onOpenBreakdown={goToServices} workspaceId={workspaceId ?? 0} />
            <StatsPanel data={data} onStatusClick={goToServices} />
          </Stack>
        </TabsPanel>
        <TabsPanel value="services">
          <Stack pt="200">
            <ServicesTabContent data={data} />
          </Stack>
        </TabsPanel>
        <TabsPanel value="data">
          <Stack gap="200" pt="200">
            <Grid columns={3} gap="200">
              <StatCard
                icon="trigger"
                label="Data throughput"
                tooltip="How fast incoming data (events, profile updates, imports) is being processed right now."
                value={formatThroughput(data.stats.throughput_per_second).replace('/s', '').replace('/min', '')}
                unit={data.stats.throughput_per_second >= 1 ? '/s' : '/min'}
              />
              <StatCard
                icon="database"
                label="Data processed today"
                tooltip="Total number of records (events, profile updates, imports) processed since midnight UTC."
                value={data.stats.processed_today.toLocaleString()}
                unit="records"
              />
              <StatCard
                icon="time-delay"
                label="Pending"
                tooltip="Total number of records waiting to be processed across all queues."
                value={[...data.processing.categories, ...data.egress.categories]
                  .reduce((sum, c) => sum + c.pending, 0)
                  .toLocaleString()}
                unit="records"
              />
            </Grid>
            <AlertsPanel alerts={data.alerts} onOpenBreakdown={goToServices} workspaceId={workspaceId ?? 0} />
            <TasksPanel data={data} />
            <Grid columns={2} gap="200" alignItems="flex-start">
              <TopEventsPanel events={topEvents} isLoading={topEventsQuery.isLoading} workspaceId={workspaceId ?? 0} />
              <SegmentsPanel segments={segments} isLoading={segmentsQuery.isLoading} workspaceId={workspaceId ?? 0} />
            </Grid>
          </Stack>
        </TabsPanel>
        <TabsPanel value="messaging">
          <Stack gap="200" pt="200">
            <Grid columns={3} gap="200">
              <StatCard
                icon="trigger"
                label="Outgoing throughput"
                tooltip={outgoingThroughputTooltip(data.stats)}
                value={formatThroughput(data.stats.outgoing_throughput_per_second)
                  .replace('/s', '')
                  .replace('/min', '')}
                unit={data.stats.outgoing_throughput_per_second >= 1 ? '/s' : '/min'}
              />
              <StatCard
                icon="database"
                label="Sent today"
                tooltip="Total outgoing messages sent since midnight UTC — includes email, push, SMS, and webhooks."
                value={data.stats.outgoing_processed_today.toLocaleString()}
                unit="records"
              />
              <StatCard
                icon="time-delay"
                label="Pending"
                tooltip="Total number of outgoing records waiting to be processed."
                value={data.egress.categories
                  .filter((c) => c.name !== 'related_updates')
                  .reduce((sum, c) => sum + c.pending, 0)
                  .toLocaleString()}
                unit="records"
              />
            </Grid>
            <MessagingTabContent alerts={data.alerts} />
          </Stack>
        </TabsPanel>
      </Tabs>
    </Grid>
  );
}
